import type { GQL_ID } from '@@partner:types:graphql'
import type { CrudFormProps, ProductGroupFormState, ProductGroupTranslation } from '@@partner:types:app'

import React, { useCallback, useEffect, useState, useRef } from 'react'
import { useFetchSingleProductGroup } from '@@partner:graphql/query/productGroup/singleProductGroup.gql'
import { useFetchAllMarketingStories } from '@@partner:graphql/query/allStories/allMarketingStories.gql'
import { useValidatableForm } from '@@partner:helpers/hooks/rsuite'
import { mergeLeft, mergeLeftOnly } from 'merge-left-utils'
import {
  makeProductGroupFormState,
  mapStateToApiVariable
} from './ProductGroupFormState'
import {
  makeGraphQLErrorNotification,
  notifyOk,
  notifyError
} from '@@partner:helpers/notification'
import { CUSTOMER_FRONTEND_URL } from '@@partner:config'

import { Form, Panel, Grid, Row, Col, IconButton, Whisper, Tooltip, SelectPicker, FlexboxGrid } from 'rsuite'
import CopyIcon from '@rsuite/icons/Copy'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'

import { productGroupFormValidationModel } from './validation/ProductGroupFormValidationModel'
import { SaveButton } from '../../common/form/Buttons'


import { commonFieldChange } from '@@partner:helpers/util'
import { useMutateSaveProductGroup } from '@@partner:graphql/mutation/productGroup/saveProductGroup.gql'
import ProductGroupTypePicker from './form/ProductGroupTypePicker'
import ProductGroupProductsTable from './ProductGroupProductsTable'
import ProductGroupAddProducts from './ProductGroupAddProducts'
import SelectLocale from '@components/common/localizedForm/SelectLocale'
import LocalizedForm from '@components/common/localizedForm/LoclalizedForm'

export interface ProductGroupProps extends CrudFormProps {
  groupId?: GQL_ID
}

export default function ProductGroupForm ({
  groupId,
  onSaved
}: ProductGroupProps) {
  const { formState, setFormState, setFormRef } = useValidatableForm<ProductGroupFormState>(
    makeProductGroupFormState
  )
  const [ selectedLanguage, setSelectedLanguage ] = useState<string>()
  const refetchProductsRef = useRef<(() => void) | undefined>()
  const {
    data,
    loading: loadingProductGroup,
    error: errorLoadingCategory
  } = useFetchSingleProductGroup({ id: groupId })

  const [ saveProductGroup, { loading: loadingSaveProductGroup } ] =
    useMutateSaveProductGroup()

  const { data: storiesData } = useFetchAllMarketingStories({
    pager: { page: 1, limit: 100 }
  })

  useEffect(() => {
    if (!loadingProductGroup && data) {
      setFormState((state) => mergeLeft(state, makeProductGroupFormState(data.singleProductGroup)))
    }
  }, [ data, loadingProductGroup, setFormState ])

  const handleFieldChange = commonFieldChange(setFormState)

  const handleFormChange = useCallback(
    <T extends keyof ProductGroupFormState>(
      formData: Record<T, ProductGroupFormState[T]>
    ) => {
      setFormState((state) => mergeLeft(state, formData))
    },
    [ setFormState ]
  )

  const handleTranslateChange = useCallback(
    (field: keyof ProductGroupTranslation) => (value: string) => {
      if (!selectedLanguage) {
        return
      }
      const newTranslations = [ ...formState.i18n ]
      const translationIndex = newTranslations.findIndex(translation => translation.language_code === selectedLanguage)
      if (translationIndex > -1) {
        newTranslations[translationIndex][field] = value
      }
      else {
        const newTranslation = mergeLeft({
          language_code: selectedLanguage,
          label: ''
        }, {
          language_code: selectedLanguage,
          [field]: value
        })
        newTranslations.push(newTranslation)
      }
      setFormState((state) => mergeLeft(state, { i18n: newTranslations }))
    },
    [ formState.i18n, selectedLanguage, setFormState ]
  )


  const handleFormSubmit = useCallback(
    (isOk = false) => {
      if (isOk) {
        saveProductGroup({
          variables: {
            productGroupInfo: mapStateToApiVariable(formState)
          }
        })
          .then(({ data }) => {
            notifyOk('Группа сохранена')
            setFormState((state) =>
              mergeLeftOnly([ 'id' ], state, data?.productGroup)
            )

            onSaved?.()
          })
          .catch(makeGraphQLErrorNotification('Ошибка сохранения данных'))
      }
      else {
        notifyError('Форма заполнена некорректно')
      }
    },
    [ formState, onSaved, saveProductGroup, setFormState ]
  )

  const generateProductGroupUrl = useCallback(() => {
    if (!formState.id) {
      return ''
    }
    return `${CUSTOMER_FRONTEND_URL}/product-groups/${formState.id}`
  }, [ formState.id ])

  if (errorLoadingCategory) {
    return (
      <GraphQLCommonErrorDisplay
        title={`Не удалось загрузить категорию #${groupId} для редактирования`}
        error={errorLoadingCategory}
      />
    )
  }

  return (
    <Form
      formValue={formState}
      onChange={handleFormChange}
      onSubmit={handleFormSubmit}
      ref={setFormRef}
      model={productGroupFormValidationModel}
      fluid
    >
      <Panel>
        <Grid fluid>
          <Row>
            <Col xs={24} md={12}>
              <Panel
                shaded
                header='Основная информация'
                className='my-4'
              >
                <Form.Group>
                  <Form.ControlLabel>Название</Form.ControlLabel>
                  <Form.Control placeholder="Название" name="name" />
                </Form.Group>
                <Form.Group>
                  <Form.ControlLabel>Лейбл</Form.ControlLabel>
                  <Form.Control placeholder='Лейбл' name='label' />
                </Form.Group>
                { generateProductGroupUrl() && (
                  <Form.Group>
                    <Form.ControlLabel>Ссылка на группу</Form.ControlLabel>
                    <div style={{ padding: '8px 12px', background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <a href={generateProductGroupUrl()} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff', textDecoration: 'none' }}>
                        {generateProductGroupUrl()}
                      </a>
                      <Whisper placement="top" trigger="click" speaker={<Tooltip>Скопировано!</Tooltip>}>
                        <IconButton
                          icon={<CopyIcon />}
                          size="xs"
                          appearance="subtle"
                          onClick={() => navigator.clipboard.writeText(generateProductGroupUrl())}
                        />
                      </Whisper>
                    </div>
                  </Form.Group>
                )}
                <Form.ControlLabel>Перевод</Form.ControlLabel>
                <SelectLocale
                  selectedLanguage={selectedLanguage}
                  onSelect={(language) => setSelectedLanguage(language)}
                  onAdd={(language) => {
                    setFormState((state) => mergeLeft(state, { languages: [ ...state.languages, language ] }))
                    setSelectedLanguage(language)
                  }}
                  languages={formState.languages}
                />
                { selectedLanguage && (
                  <LocalizedForm
                    name='label'
                    label='Лейбл'
                    selectedLanguage={selectedLanguage}
                    value={formState.i18n?.find((translation) => translation.language_code === selectedLanguage)?.label || ''}
                    onChange={handleTranslateChange('label')}
                  />
                )}
              </Panel>
            </Col>
            <Col xs={24} md={12}>
              <Panel
                shaded
                header='Тип группы'
                className='my-4'
              >

                <Form.Group>
                  <Form.ControlLabel>Тип группы</Form.ControlLabel>
                  <ProductGroupTypePicker
                    value={formState.type}
                    onChange={handleFieldChange('type')}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.ControlLabel>Маркетинг сторис</Form.ControlLabel>
                  <SelectPicker
                    data={storiesData?.allMarketingStories?.edges?.map((story) => ({
                      label: story.name,
                      value: story.id
                    })) || []}
                    value={formState.story_id}
                    onChange={handleFieldChange('story_id')}
                    placeholder="Выберите сторис"
                    block
                    searchable
                  />
                </Form.Group>
              </Panel>
            </Col>
          </Row>
        </Grid>
      </Panel>

      <SaveButton
        loading={loadingProductGroup || loadingSaveProductGroup}
        disabled={loadingProductGroup || loadingSaveProductGroup}
      >
        Cохранить
      </SaveButton>

      { groupId && (
        <Panel
          shaded
          header={
            <FlexboxGrid justify="space-between" align="middle">
              <span>Товары в группе</span>
              <ProductGroupAddProducts 
                groupId={groupId} 
                onProductsAdded={() => refetchProductsRef.current?.()}
              />
            </FlexboxGrid>
          }
          className="my-4"
        >
          <ProductGroupProductsTable groupId={groupId} refetchRef={refetchProductsRef} />
        </Panel>
      )}
    </Form>
  )
}
